import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, StyledBody } from 'baseui/card';
import { Skeleton } from 'baseui/skeleton';
import { SnackbarProvider, PLACEMENT } from 'baseui/snackbar';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import BackNav from '../../components/navigation/BackNav';
import LearnPageShell from './LearnPageShell';
import QuestionCardItem from '../../components/common/QuestionCardItem';
import Stats from '../../components/Items/Stats';
import LearnImportProgress from './LearnImportProgress';
import LearnImportReviewPanel from './LearnImportReviewPanel';
import LearnNotebookImportSection from './LearnNotebookImportSection';
import LearnNotebookFilters from './LearnNotebookFilters';
import NotebookAvatar from './NotebookAvatar';
import { invalidateRealtime } from '../../realtime/realtimeBus';
import { buildReadCountMap } from '../../utils/readHistoryCounts';
import '../../components/pages/styles/QuestionAnswer.css';
import '../../theme/qnaread-reading.css';
import LearnNotebookSharingPanel from './LearnNotebookSharingPanel';
import {
  NOTEBOOK_ACCESS,
  accessModeFromSummary,
  applyNotebookSharing,
  canSelectAccessMode,
  defaultAccessModeForPlan,
  formatSharingError,
  isTeamPlan,
  resolveAccessModeForPlan,
} from '../../utils/notebookSharing';
import { LEARN_HUB } from '../../config/appLabels';
import { isImportNeedsReview } from '../../utils/importJobStatus';
import { isNotebookOwner, resolveAuthUser } from '../../utils/notebookOwnership';
import './learn.css';

const PAGE_SIZE = 40;

export default function LearnNotebookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient, user: authUser } = useContext(AuthContext);
  const userProfile = useMemo(() => resolveAuthUser(authUser), [authUser]);
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [importDismissed, setImportDismissed] = useState(false);
  const [error, setError] = useState('');
  const [readStatus, setReadStatus] = useState({});
  const [readCounts, setReadCounts] = useState({});
  const [likeStatus, setLikeStatus] = useState({});
  const [savedIds, setSavedIds] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [subtopic, setSubtopic] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [plan, setPlan] = useState('FREE');
  const [accessMode, setAccessMode] = useState(NOTEBOOK_ACCESS.PUBLIC);
  const [memberEmail, setMemberEmail] = useState('');
  const [shareEmailError, setShareEmailError] = useState('');
  const [shareSaving, setShareSaving] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [shareMessageOk, setShareMessageOk] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const t = setTimeout(() => setStatusMessage(''), 3500);
    return () => clearTimeout(t);
  }, [statusMessage]);
  const [editingIndex] = useState(null);
  const pollRef = useRef(null);
  const importSectionRef = useRef(null);

  const isAdmin = useMemo(() => {
    const roles = userProfile?.roles || [];
    return roles.includes('ROLE_ADMIN');
  }, [userProfile]);

  const isOwner = useMemo(
    () => isNotebookOwner(summary, userProfile),
    [summary, userProfile],
  );

  const loadQuestions = useCallback(async (pageNum, append) => {
    const res = await apiClient.get(`/api/v1/learn/notebooks/${id}/questions`, {
      params: { page: pageNum, size: PAGE_SIZE },
    });
    const items = res.data?.items || [];
    setQuestions((prev) => (append ? [...prev, ...items] : items));
    setTotalQuestions(Number(res.data?.total ?? items.length));
    setHasMore(Boolean(res.data?.hasNext));
    setPage(pageNum);
  }, [apiClient, id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, hist, saved, usageRes] = await Promise.all([
        apiClient.get(`/api/v1/learn/notebooks/${id}`),
        apiClient.get('/api/get-readHistory-data'),
        apiClient.get('/api/learning/saved/ids'),
        apiClient.get('/api/users/me/usage').catch(() => ({ data: { plan: 'FREE' } })),
      ]);
      setSummary(s.data);
      const userPlan = (usageRes.data?.plan || 'FREE').toUpperCase();
      setPlan(userPlan);
      const summaryMode = accessModeFromSummary(s.data);
      setAccessMode(
        canSelectAccessMode(summaryMode, userPlan) ? summaryMode : defaultAccessModeForPlan(userPlan),
      );
      await loadQuestions(0, false);

      const histItems = hist.data?.historyItemDto || [];
      const initialRead = {};
      const initialLike = {};
      histItems.forEach((row) => {
        if (row.questionId != null) {
          initialRead[row.questionId] = row.readStatus;
          initialLike[row.questionId] = row.likeStatus;
        }
      });
      setReadStatus(initialRead);
      setReadCounts(buildReadCountMap(histItems));
      setLikeStatus(initialLike);

      const savedList = saved.data;
      if (Array.isArray(savedList)) {
        setSavedIds(Object.fromEntries(savedList.map((qid) => [qid, true])));
      }
    } catch {
      setError('Notebook not found or access denied.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, id, loadQuestions]);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  useEffect(() => {
    if (!isNotebookOwner(summary, userProfile) || !id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/v1/learn/imports');
        const jobs = res.data || [];
        const nbId = Number(id);
        const forNotebook = jobs.filter((j) => j.notebookId === nbId);
        const pendingReview = forNotebook.find((j) => isImportNeedsReview(j));
        const inFlight = forNotebook.find(
          (j) => !['COMPLETED', 'FAILED', 'CANCELLED', 'PENDING_REVIEW'].includes(j.status),
        );
        if (cancelled) return;
        if (pendingReview) {
          setImportJob(pendingReview);
          setImportDismissed(false);
        } else if (inFlight && !importJob) {
          setImportJob(inFlight);
          setImportDismissed(false);
          pollJob(inFlight.id);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only restore on notebook load
  }, [summary, userProfile, id, apiClient]);

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/api/v1/learn/imports/${jobId}`);
        setImportJob(res.data);
        setImportDismissed(false);
        const terminal = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(res.data.status);
        if (res.data.status === 'PENDING_REVIEW' || res.data.stage === 'PENDING_REVIEW') {
          clearInterval(pollRef.current);
          return;
        }
        if (terminal) {
          clearInterval(pollRef.current);
          if (res.data.status === 'COMPLETED') {
            load();
          }
        }
      } catch {
        /* keep polling — imports API may briefly 500 during schema migrations */
      }
    }, 2500);
  };

  const handleImportReviewConfirmed = (job) => {
    setImportJob(job);
    setImportDismissed(false);
    load();
  };

  const handleImportReviewDiscarded = (job) => {
    setImportJob(job);
    setImportDismissed(true);
  };

  const handleImportJobStarted = (job) => {
    setImportJob(job);
    setImportDismissed(false);
    if (job?.id) pollJob(job.id);
  };

  const subtopics = useMemo(() => {
    const set = new Set();
    questions.forEach((q) => {
      if (q.subCategory2) set.add(q.subCategory2);
    });
    return [...set].sort();
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return questions.filter((item) => {
      if (subtopic && item.subCategory2 !== subtopic) return false;
      if (difficulty && String(item.subCategory1 || item.difficultyLevel || '').toUpperCase() !== difficulty) {
        return false;
      }
      if (!q) return true;
      return (
        String(item.question || '').toLowerCase().includes(q)
        || String(item.answer || '').toLowerCase().includes(q)
        || String(item.subCategory2 || '').toLowerCase().includes(q)
      );
    });
  }, [questions, searchQuery, subtopic, difficulty]);

  const toggleReadStatus = async (questionId) => {
    const updatedStatus = !readStatus[questionId];
    const priorCount = readCounts[questionId] ?? 0;
    setReadStatus((prev) => ({ ...prev, [questionId]: updatedStatus }));
    if (updatedStatus) {
      setReadCounts((prev) => ({ ...prev, [questionId]: priorCount + 1 }));
    }
    try {
      await apiClient.patch(`/api/update-read-status/${questionId}`, {
        readStatus: updatedStatus,
        likeStatus: Boolean(likeStatus[questionId]),
        isReadOnly: true,
      });
      if (updatedStatus) {
        const { recordTeamReadProgress } = await import('../../utils/teamReadProgress');
        await recordTeamReadProgress(apiClient, questionId);
      }
      invalidateRealtime('read-toggle');
    } catch {
      setReadStatus((prev) => ({ ...prev, [questionId]: !updatedStatus }));
      setReadCounts((prev) => ({ ...prev, [questionId]: priorCount }));
    }
  };

  const handleSetReadStatus = (questionId, value) => {
    setReadStatus((prev) => {
      const wasRead = prev[questionId];
      if (value && !wasRead) {
        setReadCounts((counts) => ({
          ...counts,
          [questionId]: (counts[questionId] ?? 0) + 1,
        }));
      }
      return { ...prev, [questionId]: value };
    });
  };

  const toggleSaved = async (questionId) => {
    const next = !savedIds[questionId];
    setSavedIds((prev) => {
      const copy = { ...prev };
      if (next) copy[questionId] = true;
      else delete copy[questionId];
      return copy;
    });
    try {
      if (next) await apiClient.put(`/api/learning/saved/${questionId}`);
      else await apiClient.delete(`/api/learning/saved/${questionId}`);
    } catch {
      setSavedIds((prev) => {
        const copy = { ...prev };
        if (next) delete copy[questionId];
        else copy[questionId] = true;
        return copy;
      });
    }
  };

  const handleDeleteClick = async (index) => {
    const questionId = filteredQuestions[index]?.id;
    if (!questionId) return;
    try {
      await apiClient.delete(`/api/delete-question?questionId=${questionId}`);
      setQuestions((prev) => prev.filter((item) => item.id !== questionId));
      setStatusMessage('Question removed');
    } catch {
      setStatusMessage('Could not delete question');
    }
  };

  const handleTextareaResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      await loadQuestions(page + 1, true);
    } finally {
      setLoadingMore(false);
    }
  };

  const showImport = importJob && !importDismissed;

  const deleteNotebook = async () => {
    if (!isOwner) return;
    const title = summary.title || 'this notebook';
    const confirmed = window.confirm(
      `Delete "${title}"?\n\nAll questions, PDFs, and import jobs for this notebook will be removed. This cannot be undone.`,
    );
    if (!confirmed) return;
    setDeleteBusy(true);
    setError('');
    try {
      await apiClient.delete(`/api/v1/learn/notebooks/${id}`);
      navigate(LEARN_HUB.path, { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      const code = data?.message || data?.detail || '';
      setError(
        code === 'NOTEBOOK_DELETE_DENIED'
          ? 'Only the person who created this notebook can delete it.'
          : code || 'Could not delete this notebook.',
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const saveSharing = async () => {
    if (!isOwner) return;
    setShareMessage('');
    setShareMessageOk(false);
    setShareEmailError('');
    if (!canSelectAccessMode(accessMode, plan)) {
      setShareMessage(formatSharingError({
        response: {
          data: {
            message: accessMode === NOTEBOOK_ACCESS.PRIVATE
              ? 'PRIVATE_NOTEBOOK_REQUIRES_UPGRADE'
              : 'TEAM_NOTEBOOK_REQUIRES_TEAM_PLAN',
          },
        },
      }));
      return;
    }
    if (accessMode === NOTEBOOK_ACCESS.EMAIL) {
      const email = memberEmail.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setShareEmailError('Enter a valid collaborator email.');
        return;
      }
    }
    setShareSaving(true);
    try {
      const mode = resolveAccessModeForPlan(plan, accessMode);
      await applyNotebookSharing(apiClient, id, mode, memberEmail);
      setShareMessageOk(true);
      setShareMessage('Sharing settings updated.');
      const s = await apiClient.get(`/api/v1/learn/notebooks/${id}`);
      setSummary(s.data);
      setAccessMode(accessModeFromSummary(s.data));
    } catch (err) {
      setShareMessageOk(false);
      setShareMessage(formatSharingError(err));
    } finally {
      setShareSaving(false);
    }
  };

  return (
    <>
      <PageMeta title={summary?.title || 'Notebook'} description="Study your personal QnaRead notebook" />
      <LearnPageShell userProfile={userProfile} loading={loading && !summary}>
        <div className="learn-subpage learn-notebook-detail learn-notebook-detail-shell">
        <BackNav to={LEARN_HUB.path} label={LEARN_HUB.name} />

        {loading && (
          <div className="learn-skeleton-wrap">
            <Skeleton height="120px" animation />
            <Skeleton height="48px" animation overrides={{ Root: { style: { marginTop: 16 } } }} />
            <Skeleton height="280px" animation overrides={{ Root: { style: { marginTop: 24 } } }} />
          </div>
        )}

        {error && <p className="learn-alert" role="alert">{error}</p>}

        {!loading && summary && (
          <>
            <section className="learn-hero-panel learn-detail-panel">
              <header className="learn-detail-head">
                <div className="learn-detail-head-main">
                  <NotebookAvatar size="lg" />
                  <div className="learn-detail-head-text">
                    <p className="learn-page-eyebrow">Notebook</p>
                    <h1 className="home-minimal-title learn-detail-title">{summary.title}</h1>
                    {(summary.description || summary.moduleLabel) && (
                      <div className="learn-detail-meta">
                        {summary.description && (
                          <p className="learn-nb-desc learn-detail-desc">{summary.description}</p>
                        )}
                        {summary.moduleLabel && (
                          <span className="learn-nb-card-module">{summary.moduleLabel}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <dl className="learn-page-stats learn-detail-stats">
                  <div>
                    <dt>Questions</dt>
                    <dd>{summary.questionCount}</dd>
                  </div>
                  <div>
                    <dt>Subtopics</dt>
                    <dd>{subtopics.length}</dd>
                  </div>
                  <div>
                    <dt>Read</dt>
                    <dd>{summary.progressPercent}%</dd>
                  </div>
                </dl>
                {isOwner && (
                  <div className="learn-detail-head-actions">
                    <button
                      type="button"
                      className="home-btn learn-btn--danger learn-btn--compact"
                      onClick={deleteNotebook}
                      disabled={
                        deleteBusy
                        || Boolean(
                          showImport
                          && importJob?.status
                          && !['COMPLETED', 'FAILED', 'CANCELLED'].includes(importJob.status),
                        )
                      }
                    >
                      {deleteBusy ? 'Deleting…' : 'Delete notebook'}
                    </button>
                  </div>
                )}
              </header>
              <div className="learn-detail-progress">
                <div className="learn-nb-card-progress-head">
                  <span>Reading progress</span>
                  <span>{summary.progressPercent}%</span>
                </div>
                <div className="learn-progress learn-progress--lg" role="progressbar" aria-valuenow={summary.progressPercent} aria-valuemin={0} aria-valuemax={100}>
                  <span style={{ width: `${summary.progressPercent}%` }} />
                </div>
              </div>
            </section>

            {isOwner && (
            <section className="sh-block learn-detail-actions">
              <div className="learn-sharing-card">
                <div className="learn-sharing-card-head">
                  <h3 className="learn-sharing-title">Sharing</h3>
                  <button
                    type="button"
                    className="home-btn home-btn-primary learn-toolbar-btn learn-sharing-save-btn learn-btn--compact"
                    onClick={saveSharing}
                    disabled={shareSaving}
                  >
                    {shareSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <LearnNotebookSharingPanel
                  accessMode={accessMode}
                  onAccessModeChange={setAccessMode}
                  memberEmail={memberEmail}
                  onMemberEmailChange={setMemberEmail}
                  emailError={shareEmailError}
                  compact
                />
              </div>
              {shareMessage && (
                <p
                  className={`learn-sharing-msg${shareMessageOk ? ' learn-sharing-msg--ok' : ' learn-sharing-msg--error'}`}
                  role="status"
                >
                  {shareMessage}
                </p>
              )}
              {summary.questionCount > 0 && (
                <div className="learn-detail-actions__tools learn-toolbar">
                  <span className="learn-detail-tools-label">Study</span>
                  <Link to="/practice" className="home-btn home-btn-secondary learn-btn--compact">Practice</Link>
                  <Link to="/review" className="home-btn home-btn-secondary learn-btn--compact">Review</Link>
                  <div className="learn-detail-tools-extra">
                    <Stats topic={summary.title} data={questions} readHistory={readStatus} />
                  </div>
                </div>
              )}
            </section>
            )}

            {isOwner && (
              <div ref={importSectionRef}>
                <LearnNotebookImportSection
                  notebookId={id}
                  apiClient={apiClient}
                  onJobStarted={handleImportJobStarted}
                  onError={setError}
                  compact={summary.questionCount > 0}
                  defaultMode={summary.questionCount === 0 ? 'text' : 'pdf'}
                />
              </div>
            )}

            {isOwner && (
              <section className="sh-block learn-notebook-danger" aria-labelledby="learn-delete-heading">
                <h2 id="learn-delete-heading" className="learn-danger-title">Delete notebook</h2>
                <p className="learn-muted learn-danger-copy">
                  Only you (the creator) can delete this notebook. All questions, PDFs, and import jobs will be removed.
                  Collaborators will lose access. This cannot be undone.
                </p>
                <button
                  type="button"
                  className="home-btn learn-btn--danger learn-btn--compact"
                  onClick={deleteNotebook}
                  disabled={deleteBusy || Boolean(showImport && importJob?.status && !['COMPLETED', 'FAILED', 'CANCELLED'].includes(importJob.status))}
                >
                  {deleteBusy ? 'Deleting…' : 'Delete notebook permanently'}
                </button>
              </section>
            )}

            {!isOwner && summary && (
              <p className="learn-muted learn-detail-viewer-note">
                You are viewing a notebook shared with you. Only the creator can change sharing settings or delete it.
              </p>
            )}

            {showImport && isImportNeedsReview(importJob) && (
              <LearnImportReviewPanel
                job={importJob}
                apiClient={apiClient}
                onConfirmed={handleImportReviewConfirmed}
                onDiscarded={handleImportReviewDiscarded}
                onError={setError}
              />
            )}

            {showImport && !isImportNeedsReview(importJob) && (
              <LearnImportProgress
                job={importJob}
                onRetry={() => importSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                onDismiss={() => setImportDismissed(true)}
              />
            )}

            {questions.length === 0 && !showImport && (
              <p className="learn-muted learn-empty-import-hint">
                Use the import section above to add your first questions.
              </p>
            )}

            {questions.length > 0 && (
              <>
                <LearnNotebookFilters
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  subtopics={subtopics}
                  selectedSubtopic={subtopic}
                  onSubtopicChange={setSubtopic}
                  selectedDifficulty={difficulty}
                  onDifficultyChange={setDifficulty}
                  totalShown={filteredQuestions.length}
                  totalCount={Math.max(totalQuestions, questions.length)}
                />

                <SnackbarProvider placement={PLACEMENT.bottom}>
                  {statusMessage && (
                    <div className="learn-toast" role="status">{statusMessage}</div>
                  )}
                  <Card className="questionnaire-card-list learn-question-list">
                    <StyledBody>
                      {filteredQuestions.map((questionItem, index) => (
                        <QuestionCardItem
                          key={questionItem.id}
                          index={index}
                          questionItem={questionItem}
                          toggleReadStatus={toggleReadStatus}
                          readStatus={readStatus}
                          readCount={readCounts[questionItem.id] ?? 0}
                          liked={Boolean(likeStatus[questionItem.id])}
                          handleSetReadStatus={handleSetReadStatus}
                          handleDeleteClick={handleDeleteClick}
                          setStatusMessage={setStatusMessage}
                          editingIndex={editingIndex}
                          isAdmin={isAdmin}
                          handleTextareaResize={handleTextareaResize}
                          setQuestionData={setQuestions}
                          saved={Boolean(savedIds[questionItem.id])}
                          onToggleSave={toggleSaved}
                          userId={userProfile?.id}
                        />
                      ))}
                      {filteredQuestions.length === 0 && (
                        <p className="learn-card-meta" style={{ padding: 24 }}>No questions match your filters.</p>
                      )}
                    </StyledBody>
                  </Card>
                  {hasMore && (
                    <div className="learn-load-more">
                      <button
                        type="button"
                        className="home-btn home-btn-secondary"
                        onClick={loadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? 'Loading…' : 'Load more questions'}
                      </button>
                    </div>
                  )}
                </SnackbarProvider>
              </>
            )}

            <p className={`learn-footnote learn-detail-footnote${showImport ? ' learn-detail-footnote--with-import' : ''}`}>
              Upload content you have the right to use. Generated Q&amp;A is grounded in your document where possible.
            </p>
          </>
        )}
        </div>
      </LearnPageShell>
    </>
  );
}
